<template>
  <div class="content">
    <app-spinner v-if="!is_loaded" />
    <template v-else>
      <app-cells position="between">
        <app-cells :indent="false">
          <h1 class="title">Cash request (ID: {{ $route.params.id }})</h1>
          <div class="status-title" :class="[`status-title--${cash.status.code}`]">
            {{ cash.status.name }}
          </div>
        </app-cells>
        <app-button
          v-if="cash.status.code === 'requested' && show_controls"
          @click.native="onShowModal('delete')"
          theme="error"
        >
          Delete
        </app-button>
      </app-cells>
      <div class="info-cards">
        <div class="info-cards__card">
          <div class="info-cards__row">
            <div class="info-cards__row-item">
              <span>Date:</span> <span>{{ dateFormat(cash.date) }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Taken by: </span>
              <template v-if="cash.taken_by">
                <router-link
                    v-if="is_general_manager"
                    :to="{ name: 'employees-profile', params: { id: cash.taken_by.id } }"
                    class="info-cards__link"
                >
                  {{ cash.taken_by.full_name }}
                </router-link>
                <span v-else>{{ cash.taken_by.full_name }}</span>
              </template>
              <span v-else>-</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Given by: </span>
              <template v-if="cash.given_by">
                <router-link
                    v-if="is_general_manager"
                    :to="{ name: 'employees-profile', params: { id: cash.given_by.id } }"
                    class="info-cards__link"
                >
                  {{ cash.given_by.full_name }}
                </router-link>
                <span v-else>{{ cash.given_by.full_name }}</span>
              </template>
              <span v-else>-</span>
            </div>
          </div>
        </div>
        <div class="info-cards__card">
          <div class="info-cards__row info-cards__row--dotted">
            <span>{{ cash.status.code === 'requested' ? 'Requested amount' : 'Given amount' }}:</span>
            <span>{{ cash.status.code === 'requested' ? cash.amount.toLocaleString() : cash.amount_given.toLocaleString() }} TSh</span>
          </div>
          <div class="info-cards__row info-cards__row--dotted">
            <span>Real amount:</span><span>{{ cash.real_amount.toLocaleString() }} TSh</span>
          </div>
          <div class="info-cards__row info-cards__row--dotted">
            <span>Difference:</span><span>{{ cash.difference.toLocaleString() }} TSh</span>
          </div>
        </div>
        <div class="info-cards__card">
          <div class="info-cards__row info-cards__row--dotted">
            <span>Total orders:</span> <span>{{ cash.total_orders }}</span>
          </div>
          <div class="info-cards__row info-cards__row--dotted">
            <span>Completed orders:</span> <span>{{ cash.completed_orders }}</span>
          </div>
          <div class="info-cards__row info-cards__row--dotted">
            <span>EFD missing:</span> <span>{{ cash.efd_missing.toLocaleString() }} TSh</span>
          </div>
        </div>
      </div>
      <app-cells>
        <app-textarea
          v-model="cash.note"
          rows="5"
          @change.native="onUpdateNotes"
          placeholder="Write a notes"
          :readonly="cash.status.code === 'completed' || is_product_storage_storekeeper"
        />
      </app-cells>
      <v-client-table
        :data="table.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default"
        :class="{ 'table-default--loading': table_preloader }"
      >
        <template #h__id>
          <div class="table-default__left">ID</div>
        </template>
        <template #h__supplier>
          <div class="table-default__left">Supplier</div>
        </template>
        <template #id="{ row }">
          <div class="table-default__left">
            <router-link :to="{name: 'product-storage-order-detail', params: {id: row.id }}" class="table-default__link-border">
              Order - {{ row.id }}
            </router-link>
          </div>
        </template>
        <template #date="{ row }">
          {{ dateFormat(row.date) }}
        </template>
        <template #supplier="{ row }">
          <router-link
              v-if="is_general_manager || is_product_storage_manager || is_product_storage_supervisor"
              :to="{name: 'supplier-detail', params: {id: row.supplier.id }}"
              class="table-default__link-border"
          >
            {{ row.supplier.name }}
          </router-link>
          <div v-else class="table-default__left">{{ row.supplier.name }}</div>
        </template>
        <template #estimated="{ row }">
          {{ row.estimated.toLocaleString() }} TSh
        </template>
        <template #real="{ row }">
          {{ row.real.toLocaleString() }} TSh
        </template>
        <template #difference="{ row }">
          {{ row.difference.toLocaleString() }} TSh
        </template>
        <template #efd_provided="{ row }">{{ row.efd_provided.toLocaleString() }} TSh</template>
        <template #efd_missing="{ row }">{{ row.efd_missing.toLocaleString() }} TSh</template>
        <template #status="{ row }">
          <span class="status-tag" :style="{ 'backgroundColor': row.status.color || '#8a8a8a' }">
            {{ row.status.name }}
          </span>
        </template>
        <template v-if="cash.status.code === 'requested'" #controls="{ row }">
          <div class="table-default__controls">
            <button @click="onShowModal('order', row.id)" type="button">
              <img src="@/assets/img/delete-table-icon.svg" alt="">
            </button>
          </div>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPagination"
            v-if="pagination.pages > 1"
            v-model="pagination.page"
            :page-count="pagination.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table.items.length" :page="pagination.page" />
      </app-cells>
      <template v-if="show_controls">
        <app-cells
          v-if="cash.status.code === 'requested' || (cash.status.code === 'in_process' && cash.difference !== 0)"
          position="end"
        >
          <template v-if="cash.status.code === 'in_process' && cash.difference !== 0">
            <app-form-group :label="cash.difference > 0 ? 'Recieved by' : 'Given by'" class="form-group--no-indent" required>
              <app-select
                @search="onSelectAmountSearch"
                v-model="select_amount"
                :options="select_amount_options"
                :filterable="false"
                :clearable="false"
                :placeholder="cash.difference > 0 ? 'Recieved by' : 'Given by'"
                label="full_name"
                class="select--200"
                :error="$v.select_amount.$error"
              />
              <template #error>
                <div v-if="$v.select_amount.$dirty && !$v.select_amount.required">
                  Required field
                </div>
              </template>
            </app-form-group>
            <app-form-group
              :label="cash.difference > 0 ? 'Change amount' : 'Extra amount'"
              label-for="input_extra_amount"
              class="form-group--no-indent"
              required
            >
              <app-input
                v-model="input_extra_amount"
                id="input_extra_amount"
                type="number"
                class="input--200"
                name="input_extra_amount"
                :error="$v.input_extra_amount.$error"
              />
              <template #error>
                <div v-if="$v.input_extra_amount.$dirty && !$v.input_extra_amount.required">
                  Required field
                </div>
              </template>
            </app-form-group>
          </template>
          <app-cells v-if="cash.status.code === 'requested'" position="end" :indent="false" class="cells--start-vertical">
            <app-form-group label="Given by" class="form-group--no-indent" required>
              <app-select
                @search="onGivenSearch"
                v-model="form.given_by"
                :options="given_by_options"
                :clearable="false"
                label="full_name"
                placeholder="Given by"
                class="select--200"
                :error="$v.form.given_by.$error"
              />
              <template #error>
                <div v-if="$v.form.given_by.$dirty && !$v.form.given_by.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Taken by" class="form-group--no-indent" required>
              <app-select
                @search="onTakenSearch"
                v-model="form.taken_by"
                :options="taken_by_options"
                :clearable="false"
                label="full_name"
                placeholder="Taken by"
                class="select select--200"
                :error="$v.form.taken_by.$error"
              />
              <template #error>
                <div v-if="$v.form.taken_by.$dirty && !$v.form.taken_by.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Amount given" label-for="input_amount" class="form-group--no-indent" required>
              <app-input
                v-model="input_amount"
                id="input_amount"
                type="number"
                class="input--200"
                name="input_amount"
                :error="$v.input_amount.$error"
              />
              <template #error>
                <div v-if="$v.input_amount.$dirty && !$v.input_amount.required">
                  Required field
                </div>
              </template>
            </app-form-group>
          </app-cells>
        </app-cells>
        <app-cells
          v-if="cash.status.code !== 'completed'"
          position="end"
        >
          <template v-if="cash.status.code === 'in_process'">
            <app-button @click.native="onPrint" theme="warn" ref="print">
              Print cash request
            </app-button>
            <app-button @click.native="onShowModal('in_process')">
              Complete
            </app-button>
          </template>
          <app-button v-if="cash.status.code === 'requested'" @click.native="onShowModal('requested')">
            Give cash
          </app-button>
        </app-cells>
      </template>
    </template>
    <modal
      name="control-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="onHideModal" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          <span v-if="modal_type === 'requested'">Give cash for orders</span>
          <span v-else-if="modal_type === 'in_process'">Complete cash request</span>
          <span v-else-if="modal_type === 'order'">Delete order</span>
          <span v-else>Delete cash request</span>
        </div>
        <div class="popup__body">
          <div class="popup__subt">
            <span v-if="modal_type === 'requested'">
              Are you sure you want to do that? Orders will mark as paid and it cannot be undone
            </span>
            <span v-else-if="modal_type === 'in_process'">Are you sure you want to do that?</span>
            <span v-else-if="modal_type === 'order'">Are you sure you want to do that?</span>
            <span v-else>Are you sure you want to delete this cash request?</span>
          </div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="onHideModal" theme="dark">Cancel</app-button>
            <app-button v-if="modal_type === 'requested'" @click.native="onSetStatus('in_process')" ref="load">Yes</app-button>
            <app-button v-else-if="modal_type === 'in_process'" @click.native="onSetStatus('completed')" ref="load">Yes</app-button>
            <app-button v-else-if="modal_type === 'order'" @click.native="onDeleteOrder" ref="load">Yes</app-button>
            <app-button v-else @click.native="onDeleteCash" ref="load">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  deleteProductStorageCashRequest,
  deleteProductStorageCashRequestOrder,
  getCashRequestPrint,
  getEmployeesListSearch,
  getOrdersProduct,
  getProductStorageCashRequestDetail,
  patchProductStorageCashRequest,
} from '@/http'
import { format, parse } from 'date-fns'
import debounce from 'lodash/debounce'
import { requiredIf } from 'vuelidate/lib/validators'

export default {
  name: 'ProductStorageCashDetail',
  data() {
    return {
      is_loaded: false,
      cash: {},
      products: [],
      table: {
        items: [],
        columns: ['id', 'date', 'supplier', 'estimated', 'real', 'difference', 'efd_provided', 'efd_missing', 'status', 'controls'],
        options: {
          headings: {
            'id': 'ID',
            'date': 'Date',
            'supplier': 'Supplier',
            'estimated': 'Est. Value',
            'real': 'Real Value',
            'difference': 'Diff. Value',
            'efd_provided': 'EFD provided',
            'efd_missing': 'EFD missing',
            'status': 'Status',
            'controls': '',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      modal_type: '',
      order_to_delete: null,
      taken_by_options: [],
      given_by_options: [],
      select_amount_options: [],
      form: {},
      input_amount: null,
      input_extra_amount: null,
      select_amount: '',
      table_preloader: false
    }
  },
  validations: {
    form: {
      given_by: {
        required: requiredIf(function() {
          return this.cash.status.code === 'requested'
        })
      },
      taken_by: {
        required: requiredIf(function() {
          return this.cash.status.code === 'requested'
        })
      }
    },
    input_amount: {
      required: requiredIf(function() {
        return this.cash.status.code === 'requested'
      })
    },
    input_extra_amount: {
      required: requiredIf(function() {
        return this.cash.status.code === 'in_process' && this.cash.difference !== 0
      })
    },
    select_amount: {
      required: requiredIf(function() {
        return this.cash.status.code === 'in_process' && this.cash.difference !== 0
      })
    },
  },
  computed: {
    show_controls() {
      return this.$store.state.userGroups.some(group => group.code === 'product_storage_manager' || group.code === 'product_storage_supervisor' || group.code === 'general_manager')
    },
    is_product_storage_storekeeper() {
      return this.$store.state.userGroups.map(item => item.code).includes('product_storage_storekeeper')
    },
    is_product_storage_manager() {
      return this.$store.state.userGroups.some(group => group.code === 'product_storage_manager')
    },
    is_product_storage_supervisor() {
      return this.$store.state.userGroups.some(group => group.code === 'product_storage_supervisor')
    },
    is_general_manager() {
      return this.$store.state.userGroups.some(group => group.code === 'general_manager')
    },
  },
  created() {
    document.title = `Cash request (ID: ${ this.$route.params.id }) | Altezza Board`
    this.getCash()
  },
  methods: {
    onPrint() {
      this.$refs.print.preload = true
      getCashRequestPrint(this.$route.params.id)
        .finally(() => { this.$refs.print.preload = false })
        .then(response => { window.open(response.data) })
    },
    onUpdateNotes() {
      patchProductStorageCashRequest(this.$route.params.id, { note: this.cash.note })
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Notes saved',
          })
        })
    },
    getCash() {
      getProductStorageCashRequestDetail(this.$route.params.id)
        .then(response => {
          this.fetchItems()
          this.cash = response.data
          this.input_amount = response.data.amount
          this.input_extra_amount = Math.abs(response.data.difference)
        })
    },
    fetchItems() {
      getOrdersProduct({ page: this.pagination.page, page_size: 20, cash_request: this.$route.params.id })
        .then(response => {
          this.table.items = response.data.results
          this.pagination.pages = response.data.pages
          this.pagination.count = response.data.count
          this.is_loaded = true
          this.table_preloader = false
        })
    },
    dateFormat(date) {
      return format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy')
    },
    onClickPagination(page) {
      this.table_preloader = true
      this.pagination.page = page
      this.fetchItems()
    },
    onSetStatus(status) {
      this.$refs.load.preload = true
      const data = {
        status
      }
      if (status === 'in_process') {
        data.amount_given = this.input_amount
        data.given_by = this.form.given_by.id
        data.taken_by = this.form.taken_by.id
      }
      if (status === 'completed' && this.cash.difference !== 0) {
        data.amount_extra = this.input_extra_amount
        data.responsible = this.select_amount.id
      }
      patchProductStorageCashRequest(this.$route.params.id, data)
        .finally(() => { this.$refs.load.preload = false })
        .then(() => {
          this.getCash()
          this.$notify({
            type: 'success',
            title: 'Cash request updated',
          })
          this.onHideModal()
          this.$v.$reset()
        })
    },
    onDeleteCash() {
      this.$refs.load.preload = true
      deleteProductStorageCashRequest(this.$route.params.id)
        .finally(() => { this.$refs.load.preload = false })
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Cash request deleted',
          })
          this.$router.push({name: 'product-storage-cash-active'})
        })
    },
    onDeleteOrder() {
      this.$refs.load.preload = true
      deleteProductStorageCashRequestOrder(this.$route.params.id, this.order_to_delete)
        .finally(() => { this.$refs.load.preload = false })
        .then(() => {
          this.getCash()
          this.order_to_delete = null
          this.$notify({
            type: 'success',
            title: 'Order deleted',
          })
          this.onHideModal()
        })
    },
    onShowModal(type, id = null) {
      if (type === 'in_process' || type === 'requested') {
        this.$v.$touch()
        if (this.$v.$invalid) {
          this.$notify({
            type: 'error',
            title: 'Check that the fields are filled in correctly',
          })
          return
        }
      }
      this.modal_type = type
      if (id) this.order_to_delete = id
      this.$modal.show('control-modal')
    },
    onHideModal() {
      this.$modal.hide('control-modal')
    },
    onTakenSearch(search, loading) {
      loading(true)
      this.searchTaken(loading, search, this)
    },
    searchTaken: debounce((loading, search, vm) => {
      if (!search) {
        loading(false)
        vm.taken_by_options = []
      } else {
        getEmployeesListSearch({ search, employment_status: 'working' }).then(response => {
          vm.taken_by_options = response.data
          loading(false)
        })
      }
    }, 350),
    onGivenSearch(search, loading) {
      loading(true)
      this.searchGiven(loading, search, this)
    },
    searchGiven: debounce((loading, search, vm) => {
      if (!search) {
        loading(false)
        vm.given_by_options = []
      } else {
        getEmployeesListSearch({ search, employment_status: 'working' }).then(response => {
          vm.given_by_options = response.data
          loading(false)
        })
      }
    }, 350),
    onSelectAmountSearch(search, loading) {
      loading(true)
      this.searchAmountSelect(loading, search, this)
    },
    searchAmountSelect: debounce((loading, search, vm) => {
      if (!search) {
        loading(false)
        vm.select_amount_options = []
      } else {
        getEmployeesListSearch({ search, employment_status: 'working' }).then(response => {
          vm.select_amount_options = response.data
          loading(false)
        })
      }
    }, 350),
  }
}
</script>
